/**
 * @file service-request.types.ts
 * @module app/modules/service-request/types/service-request.types
 * @description Types for service requests, matching the backend's service requests
 * collection and the /service-request API.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

/** Request lifecycle; same numbers as the backend ServiceRequestStatus. */
export enum ServiceRequestStatus {
  Pending = 1,
  Processing = 2,
  Completed = 3,
  Declined = 4,
  Failed = 5,
  Withdrawn = 6,
  SentToJira = 7,
}

/** Action key from the server catalog, e.g. "cancelOrder". A plain string so a new action needs no type change here. */
export type ServiceRequestActionKey = string;

/** Input kinds the form can render. A new kind needs an entry here and in components/fields/field-registry.ts. */
export type ServiceRequestFieldKind = 'orderRefKeys';

/** One input an action asks for. The server sends these with each action. */
export interface ServiceRequestField {
  /** Input property the value is saved under, e.g. "refKeys". */
  name: string;
  kind: ServiceRequestFieldKind;
  label: string;
  required?: boolean;
  helpText?: string;
  /** Most items a list input accepts. */
  maxItems?: number;
}

/** An action the user can request or approve, as the server describes it. */
export interface ServiceRequestActionDefinition {
  key: ServiceRequestActionKey;
  label: string;
  description: string;
  fields: ServiceRequestField[];
}

/** Field values keyed by field name. Each field kind decides its own value shape. */
export type ServiceRequestPayload = Record<string, unknown>;

/** "mine" lists the caller's own requests; "approvals" lists requests the caller can decide. */
export type ServiceRequestScope = 'mine' | 'approvals';

export type ServiceRequestTab = 'pending' | 'history';

/** A change a user can make to a request; each is a PATCH /service-request/:_id/<step>. */
export type ServiceRequestStep =
  | 'approve'
  | 'decline'
  | 'withdraw'
  | 'send-to-jira';

/** An admin as saved on a request: id, name, and role at that moment. */
export interface ServiceRequestActor {
  reference: string;
  name?: string;
  role?: { reference?: string; name?: string };
}

/** What the action reported when it ran on approval. */
export interface ServiceRequestResult {
  success: boolean;
  /** One-line outcome, e.g. "2 orders cancelled", or the failure reason. */
  message?: string;
  /** Records the action changed, e.g. order refKeys. */
  updatedRefs?: string[];
  failedRefs?: { refKey: string; reason: string }[];
  completedAt?: string;
}

/** The Jira ticket made for a request. No key yet means it is still being created. */
export interface ServiceRequestJiraTicket {
  key?: string;
  url?: string;
  /** False when no assignee is set in Settings, or Jira could not assign it. */
  assigned?: boolean;
  reason?: string;
  sentBy?: ServiceRequestActor;
  sentAt?: string;
}

export interface ServiceRequest {
  _id: string;
  /** Human-readable request number, for example SR000012. */
  refKey: string;
  action: ServiceRequestActionKey;
  /** Action label saved with the request, so history still reads well if the action is renamed or removed. */
  actionLabel: string;
  status: ServiceRequestStatus;
  statusUpdatedAt?: string;
  input: ServiceRequestPayload;
  requestReason: string;
  requestedBy: ServiceRequestActor;
  warehouse?: { reference: string; name?: string };
  decidedBy?: ServiceRequestActor;
  decidedAt?: string;
  decisionComment?: string;
  result?: ServiceRequestResult;
  withdrawReason?: string;
  withdrawnAt?: string;
  jiraTicket?: ServiceRequestJiraTicket;
  createdAt: string;
}

export interface ServiceRequestListParams {
  scope: ServiceRequestScope;
  tab: ServiceRequestTab;
}

export interface ServiceRequestListResponse {
  total: number;
  records: ServiceRequest[];
}

export interface CreateServiceRequestPayload {
  action: ServiceRequestActionKey;
  input: ServiceRequestPayload;
  requestReason: string;
}

/** Actions the signed-in user's role can request and approve (Settings > General > Service Request). */
export interface ServiceRequestAccess {
  requestable: ServiceRequestActionDefinition[];
  approvable: ServiceRequestActionDefinition[];
  /** True when this role can approve something and Jira is set up on the server. */
  canSendToJira: boolean;
}

export interface ServiceRequestCounts {
  /** My open requests (Pending or Processing). */
  minePending: number;
  /** Others' pending requests my role can approve. */
  approvalsPending: number;
  /** My requests decided since I last opened History (time kept in this browser). */
  newDecisions: number;
}
